import { useState } from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Solution } from '../types';

interface SolutionCardProps {
  solution: Solution;
  onCompare: (id: string) => void;
  onDemo: (id: string) => void;
  isComparing: boolean;
}

const SolutionCard = ({ solution, onCompare, onDemo, isComparing }: SolutionCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getCategoryColor = (category: string) => {
    const colors = {
      'retail': 'bg-blue-100 text-blue-800',
      'traffic': 'bg-green-100 text-green-800',
      'security': 'bg-red-100 text-red-800',
      'business-intelligence': 'bg-purple-100 text-purple-800'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-card rounded-lg shadow-brand hover:shadow-brand-lg transition-all duration-300 overflow-hidden border border-border">
      <div className="relative h-48 overflow-hidden">
        <Image
          src={solution.image}
          alt={solution.alt}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
        />
        <div className="absolute top-4 right-4">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(solution.category)}`}>
            {solution.category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
          </span>
        </div>
        {solution.demoAvailable && (
          <div className="absolute bottom-4 left-4">
            <span className="px-3 py-1 bg-success text-success-foreground rounded-full text-xs font-medium flex items-center gap-1">
              <Icon name="Play" size={14} />
              Live Demo
            </span>
          </div>
        )}
      </div>

      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-xl font-bold text-foreground">{solution.title}</h3>
          <button
            onClick={() => onCompare(solution.id)}
            className={`p-2 rounded-md transition-colors duration-300 ${
              isComparing ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-primary/10'
            }`}
            aria-label="Add to comparison"
          >
            <Icon name="GitCompare" size={20} />
          </button>
        </div>

        <p className="text-text-secondary text-sm mb-4 line-clamp-2">{solution.description}</p>

        <div className="flex items-center gap-4 mb-4 text-sm">
          <div className="flex items-center gap-1">
            <Icon name="Target" size={16} className="text-success" />
            <span className="text-foreground font-medium">{solution.accuracy}% Accuracy</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="Clock" size={16} className="text-primary" />
            <span className="text-text-secondary">{solution.deploymentTime}</span>
          </div>
        </div>

        {!isExpanded ? (
          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">Key Features</h4>
              <div className="flex flex-wrap gap-2">
                {solution.features.slice(0, 3).map((feature, index) => (
                  <span key={index} className="px-2 py-1 bg-muted text-text-secondary text-xs rounded">
                    {feature}
                  </span>
                ))}
                {solution.features.length > 3 && (
                  <span className="px-2 py-1 bg-muted text-text-secondary text-xs rounded">
                    +{solution.features.length - 3} more
                  </span>
                )}
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              fullWidth
              iconName="ChevronDown"
              iconPosition="right"
              onClick={() => setIsExpanded(true)}
            >
              View Details
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">All Features</h4>
              <div className="flex flex-wrap gap-2">
                {solution.features.map((feature, index) => (
                  <span key={index} className="px-2 py-1 bg-muted text-text-secondary text-xs rounded">
                    {feature}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">Benefits</h4>
              <ul className="space-y-1">
                {solution.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-text-secondary">
                    <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">Technical Specifications</h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-muted p-2 rounded">
                  <span className="text-text-secondary">Algorithm:</span>
                  <p className="text-foreground font-medium">{solution.technicalSpecs.algorithm}</p>
                </div>
                <div className="bg-muted p-2 rounded">
                  <span className="text-text-secondary">Speed:</span>
                  <p className="text-foreground font-medium">{solution.technicalSpecs.processingSpeed}</p>
                </div>
                <div className="bg-muted p-2 rounded">
                  <span className="text-text-secondary">Accuracy:</span>
                  <p className="text-foreground font-medium">{solution.technicalSpecs.accuracy}</p>
                </div>
                <div className="bg-muted p-2 rounded">
                  <span className="text-text-secondary">Scalability:</span>
                  <p className="text-foreground font-medium">{solution.technicalSpecs.scalability}</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">Integrations</h4>
              <div className="flex flex-wrap gap-2">
                {solution.integrations.map((integration, index) => (
                  <span key={index} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded flex items-center gap-1">
                    <Icon name="Plug" size={12} />
                    {integration}
                  </span>
                ))}
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              fullWidth
              iconName="ChevronUp"
              iconPosition="right"
              onClick={() => setIsExpanded(false)}
            >
              Show Less
            </Button>
          </div>
        )}

        <div className="flex gap-2 mt-4 pt-4 border-t border-border">
          {solution.demoAvailable && (
            <Button
              variant="default"
              size="sm"
              fullWidth
              iconName="Play"
              iconPosition="left"
              onClick={() => onDemo(solution.id)}
            >
              Try Demo
            </Button>
          )}
          <Link to="/implementation-hub" className="flex-1">
            <Button variant="outline" size="sm" fullWidth>
              Learn More
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SolutionCard;